import { useState, useEffect, useRef } from "react";
import { BRAND } from "../constants/brand";

const NAV_ITEMS = {
  About: ["About Us", "Leadership Team", "Careers", "Newsroom", "Join Our Lab Network" /*, "Licenses" */],
  Providers: ["Billing and Coding", "Insurance", "Logistics", "Medical Necessity", /* "Portal Access", */ "Test Index", "Annual Notice"],
  Patient: ["Find Patient Results", "Insurance", "Make an Appointment", "Patient Billing"],
};

const ROUTE_OVERRIDES = {
  "Join Our Lab Network": "lab-acquisitions",
  "Patient Insurance": "patient-insurance",
};

// Items under the Patient group that share a name with a provider page
const PATIENT_OVERRIDES = {
  "Insurance": "patient-insurance",
};

const getRoute = (item, group) => {
  if (group === "Patient" && PATIENT_OVERRIDES[item]) return PATIENT_OVERRIDES[item];
  return ROUTE_OVERRIDES[item] || item.toLowerCase().replace(/ /g, "-").replace(/\//g, "");
};

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 4, transition: "transform 0.25s ease" }}>
    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function Navbar({ setPage, currentPage }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const go = (route) => {
    setPage(route);
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileExpanded(null);
  };

  const handleMouseEnter = (group) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(group);
  };
  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  const navBg = scrolled
    ? "rgba(0,14,36,0.96)"
    : "rgba(0,30,77,0.85)";

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        background: navBg,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: `1px solid rgba(201,169,110,${scrolled ? "0.25" : "0.12"})`,
        boxShadow: scrolled ? "0 8px 40px rgba(0,0,0,0.4)" : "none",
        transition: "background 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease",
      }}>
        {/* Top accent line */}
        <div style={{
          height: 2,
          background: "linear-gradient(90deg, transparent, #c9a96e 30%, #dfc08a 50%, #c9a96e 70%, transparent)",
          animation: "shimmer 4s linear infinite",
          backgroundSize: "200% auto",
        }} />

        <div style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "0 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 76,
        }}>

          {/* Logo */}
          <div
            onClick={() => go("home")}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0 }}
          >
            <img
              src="/logo-full-dark.png"
              alt="Labarium Diagnostic Systems"
              style={{ height: 58, width: "auto", display: "block", filter: "brightness(1.05)" }}
            />
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle navigation"
            style={{
              display: "none",
              background: "none", border: "none", cursor: "pointer",
              color: BRAND.gold, padding: 8, borderRadius: 8,
              flexDirection: "column", gap: 5, alignItems: "center", justifyContent: "center",
            }}
            className="mobile-hamburger"
          >
            <span style={{ display: "block", width: 22, height: 2, background: "currentColor", transition: "all 0.25s ease", transform: mobileOpen ? "translateY(7px) rotate(45deg)" : "none" }} />
            <span style={{ display: "block", width: 22, height: 2, background: "currentColor", transition: "all 0.25s ease", opacity: mobileOpen ? 0 : 1 }} />
            <span style={{ display: "block", width: 22, height: 2, background: "currentColor", transition: "all 0.25s ease", transform: mobileOpen ? "translateY(-7px) rotate(-45deg)" : "none" }} />
          </button>

          {/* Desktop nav */}
          <div style={{ display: "flex", alignItems: "center", gap: 2 }} className="desktop-nav">
            {Object.entries(NAV_ITEMS).map(([group, items]) => (
              <div
                key={group}
                style={{ position: "relative" }}
                onMouseEnter={() => handleMouseEnter(group)}
                onMouseLeave={handleMouseLeave}
              >
                <button style={{
                  background: openMenu === group ? "rgba(201,169,110,0.12)" : "none",
                  border: "none",
                  cursor: "pointer",
                  color: openMenu === group ? BRAND.gold : "rgba(255,255,255,0.85)",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: 13,
                  letterSpacing: "0.04em",
                  padding: "8px 14px",
                  borderRadius: 8,
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                }}>
                  {group}
                  <ChevronDown />
                </button>

                {openMenu === group && (
                  <div style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "rgba(4,18,44,0.97)",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                    borderRadius: 14,
                    minWidth: 220,
                    boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(201,169,110,0.2)",
                    overflow: "hidden",
                    zIndex: 200,
                    animation: "dropdownOpen 0.2s cubic-bezier(.4,0,.2,1)",
                  }}
                    onMouseEnter={() => clearTimeout(closeTimer.current)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* Dropdown top accent */}
                    <div style={{ height: 1, background: "linear-gradient(90deg, transparent, #c9a96e, transparent)" }} />
                    {items.map((item, idx) => (
                      <button
                        key={item}
                        onClick={() => go(getRoute(item, group))}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          width: "100%",
                          textAlign: "left",
                          padding: "12px 20px",
                          background: "none",
                          border: "none",
                          borderBottom: idx < items.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 13,
                          fontWeight: 400,
                          color: "rgba(255,255,255,0.75)",
                          cursor: "pointer",
                          transition: "all 0.18s ease",
                          letterSpacing: "0.02em",
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = "rgba(201,169,110,0.12)";
                          e.currentTarget.style.color = BRAND.gold;
                          e.currentTarget.style.paddingLeft = "26px";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = "none";
                          e.currentTarget.style.color = "rgba(255,255,255,0.75)";
                          e.currentTarget.style.paddingLeft = "20px";
                        }}
                      >
                        <span style={{ width: 4, height: 4, borderRadius: "50%", background: BRAND.gold, flexShrink: 0, opacity: 0.6 }} />
                        {item}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={() => go("contact-us")}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "rgba(255,255,255,0.85)",
                fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: 13,
                letterSpacing: "0.04em", padding: "8px 14px", borderRadius: 8,
                transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = BRAND.gold; e.currentTarget.style.background = "rgba(201,169,110,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.85)"; e.currentTarget.style.background = "none"; }}
            >
              Contact
            </button>

            <button
              onClick={() => go("labarium-intelligence")}
              style={{
                background: currentPage === "labarium-intelligence" ? "rgba(201,169,110,0.1)" : "none",
                border: "none",
                cursor: "pointer",
                color: currentPage === "labarium-intelligence" ? BRAND.gold : "rgba(255,255,255,0.85)",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500, fontSize: 13,
                letterSpacing: "0.04em",
                padding: "8px 14px",
                borderRadius: 8,
                transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = BRAND.gold; e.currentTarget.style.background = "rgba(201,169,110,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = currentPage === "labarium-intelligence" ? BRAND.gold : "rgba(255,255,255,0.85)"; e.currentTarget.style.background = currentPage === "labarium-intelligence" ? "rgba(201,169,110,0.1)" : "none"; }}
            >
              Labarium AI
            </button>

            {/* Divider */}
            <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.1)", margin: "0 6px" }} />

            <a
              href="https://portal.labarium.com/register"
              target="_blank" rel="noreferrer"
              style={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.65)",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500, fontSize: 12,
                letterSpacing: "0.04em",
                padding: "7px 12px",
                borderRadius: 7,
                border: "1px solid rgba(255,255,255,0.12)",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(201,169,110,0.4)"; e.currentTarget.style.color = BRAND.gold; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "rgba(255,255,255,0.65)"; }}
            >
              Register — AI Portal
            </a>

            {/* Pay Online button hidden per brand update
            <a
              href="https://patient.xifin.net/peportal/pages/login/?cn=medlake&tn=medlake#/login"
              target="_blank" rel="noreferrer"
              style={{
                textDecoration: "none",
                background: "linear-gradient(135deg, #c9a96e, #dfc08a)",
                backgroundSize: "200% auto",
                color: BRAND.navyDeep,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600, fontSize: 12,
                letterSpacing: "0.06em",
                padding: "8px 18px",
                borderRadius: 8,
                transition: "all 0.3s ease",
                boxShadow: "0 4px 16px rgba(201,169,110,0.3)",
              }}
              className="btn-shimmer"
            >
              Pay Online
            </a>
            */}

            <button
              onClick={() => go("make-an-appointment")}
              style={{
                background: "transparent",
                border: `1.5px solid ${BRAND.gold}`,
                cursor: "pointer",
                color: BRAND.gold,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600, fontSize: 12,
                letterSpacing: "0.06em",
                padding: "7px 16px",
                borderRadius: 8,
                transition: "all 0.25s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = BRAND.gold; e.currentTarget.style.color = BRAND.navyDeep; e.currentTarget.style.boxShadow = "0 4px 20px rgba(201,169,110,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = BRAND.gold; e.currentTarget.style.boxShadow = "none"; }}
            >
              Appointment
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile slide-down menu */}
      {mobileOpen && (
        <div style={{
          position: "fixed", top: 78, left: 0, right: 0, zIndex: 999,
          background: "rgba(0,14,36,0.98)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(201,169,110,0.2)",
          maxHeight: "calc(100vh - 78px)",
          overflowY: "auto",
          animation: "dropdownOpen 0.2s cubic-bezier(.4,0,.2,1)",
        }}>
          <div style={{ padding: "16px 0 24px" }}>
            {Object.entries(NAV_ITEMS).map(([group, items]) => (
              <div key={group}>
                <button
                  onClick={() => setMobileExpanded(e => e === group ? null : group)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    width: "100%", padding: "14px 24px",
                    background: "none", border: "none", cursor: "pointer",
                    fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14,
                    color: mobileExpanded === group ? BRAND.gold : "rgba(255,255,255,0.85)",
                    letterSpacing: "0.04em",
                  }}
                >
                  {group}
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transition: "transform 0.2s ease", transform: mobileExpanded === group ? "rotate(180deg)" : "none" }}>
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {mobileExpanded === group && (
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)", background: "rgba(255,255,255,0.02)" }}>
                    {items.map(item => (
                      <button
                        key={item}
                        onClick={() => go(getRoute(item, group))}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          width: "100%", padding: "12px 36px",
                          background: "none", border: "none", cursor: "pointer",
                          fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 400,
                          color: "rgba(255,255,255,0.65)", textAlign: "left", letterSpacing: "0.02em",
                        }}
                      >
                        <span style={{ width: 4, height: 4, borderRadius: "50%", background: BRAND.gold, opacity: 0.5, flexShrink: 0 }} />
                        {item}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={() => go("contact-us")}
              style={{
                display: "flex", alignItems: "center", width: "100%",
                padding: "14px 24px", background: "none", border: "none", cursor: "pointer",
                fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14,
                color: "rgba(255,255,255,0.85)", letterSpacing: "0.04em",
              }}
            >
              Contact
            </button>

            <button
              onClick={() => go("labarium-intelligence")}
              style={{
                display: "flex", alignItems: "center", width: "100%",
                padding: "14px 24px", background: "none",
                border: "none", borderTop: "1px solid rgba(255,255,255,0.05)", cursor: "pointer",
                fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14,
                color: "rgba(255,255,255,0.85)", letterSpacing: "0.04em",
              }}
            >
              Labarium AI
            </button>

            <div style={{ padding: "16px 24px", display: "flex", gap: 8, borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: 8, flexWrap: "wrap" }}>
              <button
                onClick={() => go("make-an-appointment")}
                style={{
                  flex: 1, minWidth: 100,
                  background: "linear-gradient(135deg, #c9a96e, #dfc08a)",
                  color: BRAND.navyDeep,
                  border: "none", borderRadius: 8, cursor: "pointer",
                  fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 12,
                  letterSpacing: "0.06em", padding: "12px 10px",
                  textTransform: "uppercase",
                }}
              >
                Appointment
              </button>
              {/* Pay Online mobile button hidden per brand update
              <a
                href="https://patient.xifin.net/peportal/pages/login/?cn=medlake&tn=medlake#/login"
                target="_blank" rel="noreferrer"
                style={{
                  flex: 1, minWidth: 90, textAlign: "center",
                  background: "rgba(201,169,110,0.1)",
                  color: BRAND.gold,
                  border: `1px solid rgba(201,169,110,0.3)`,
                  borderRadius: 8,
                  fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 12,
                  letterSpacing: "0.06em", padding: "12px 10px",
                  textDecoration: "none", textTransform: "uppercase",
                }}
              >
                Pay Online
              </a>
              */}
              <a
                href="https://portal.labarium.com/register"
                target="_blank" rel="noreferrer"
                style={{
                  flex: 1, minWidth: 90, textAlign: "center",
                  background: "rgba(255,255,255,0.06)",
                  color: "rgba(255,255,255,0.75)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 8,
                  fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 12,
                  letterSpacing: "0.06em", padding: "12px 10px",
                  textDecoration: "none", textTransform: "uppercase",
                }}
              >
                AI Portal
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-hamburger { display: flex !important; }
        }
      `}</style>
    </>
  );
}
